import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const AdminExchangeRatesScreen = () => {
  const [exchangeRates, setExchangeRates] = useState([
    { currency: 'USD', rate: 1.0000, lastUpdated: '2024-01-15' },
    { currency: 'EUR', rate: 0.9200, lastUpdated: '2024-01-15' },
    { currency: 'GBP', rate: 0.7900, lastUpdated: '2024-01-15' },
    { currency: 'CAD', rate: 1.3500, lastUpdated: '2024-01-15' },
    { currency: 'NGN', rate: 1500.0000, lastUpdated: '2024-01-15' }
  ]);
  const [newRate, setNewRate] = useState({
    currency: '',
    rate: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewRate(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddRate = async (e) => {
    e.preventDefault();
    
    if (!newRate.currency || !newRate.rate) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsLoading(true);
    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const rate = {
        currency: newRate.currency.toUpperCase(),
        rate: parseFloat(newRate.rate),
        lastUpdated: newRate.date
      };
      
      setExchangeRates(prev => [...prev, rate]);
      setNewRate({
        currency: '',
        rate: '',
        date: new Date().toISOString().split('T')[0]
      });
      
      toast.success('Exchange rate added successfully!');
    } catch (error) {
      toast.error('An error occurred while adding the exchange rate');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateRate = async (currency, newRateValue) => {
    setIsLoading(true);
    try {
      // Mock API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setExchangeRates(prev => 
        prev.map(rate => 
          rate.currency === currency 
            ? { ...rate, rate: newRateValue, lastUpdated: new Date().toISOString().split('T')[0] }
            : rate
        )
      );
      
      toast.success('Exchange rate updated successfully!');
    } catch (error) {
      toast.error('An error occurred while updating the exchange rate');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRate = async (currency) => {
    if (window.confirm(`Are you sure you want to delete the exchange rate for ${currency}?`)) {
      setIsLoading(true);
      try {
        // Mock API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setExchangeRates(prev => prev.filter(rate => rate.currency !== currency));
        toast.success('Exchange rate deleted successfully!');
      } catch (error) {
        toast.error('An error occurred while deleting the exchange rate');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('is_admin');
    navigate('/admin');
  };

  return (
    <div className="container">
      <div className="admin-header-fullwidth">
        <div className="admin-header-content">
          <div className="admin-header-left">
            <img src="/logo.svg" alt="Project 31 Logo" className="admin-logo" />
            
          </div>
          
          <div className="admin-nav">
            <a href="/admin/dashboard">Dashboard</a>
            <a href="/admin/transactions">Transactions</a>
            <a href="/admin/manage-accounts">Accounts</a>
            <a href="/admin/bitcoins">Bitcoins</a>
          </div>
        </div>
      </div>

        <div className="dashboard-content admin-dashboard-content">
        <div className="exchange-rates-section">
          <h2>Exchange Rates Management</h2>
          
          <div className="add-rate-form">
            <h3>Add New Exchange Rate</h3>
            <form onSubmit={handleAddRate} className="rate-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="currency">Currency</label>
                  <input
                    type="text"
                    id="currency"
                    name="currency"
                    value={newRate.currency}
                    onChange={handleInputChange}
                    placeholder="e.g., USD, EUR, GBP"
                    required
                    disabled={isLoading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="rate">Rate (to USDT)</label>
                  <input
                    type="number"
                    id="rate"
                    name="rate"
                    value={newRate.rate}
                    onChange={handleInputChange}
                    placeholder="Enter exchange rate"
                    step="0.0001"
                    required
                    disabled={isLoading}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="date">Date</label>
                  <input
                    type="date"
                    id="date"
                    name="date"
                    value={newRate.date}
                    onChange={handleInputChange}
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
              
              <div className="form-actions">
                <button
                  type="submit"
                  className="add-rate-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Adding...' : 'Add Exchange Rate'}
                </button>
              </div>
            </form>
          </div>

          <div className="rates-table-container">
            <h3>Current Exchange Rates</h3>
            <table className="rates-table">
              <thead>
                <tr>
                  <th>Currency</th>
                  <th>Rate (to USDT)</th>
                  <th>Last Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {exchangeRates.map((rate, index) => (
                  <tr key={index}>
                    <td>{rate.currency}</td>
                    <td>{rate.rate.toFixed(4)}</td>
                    <td>{rate.lastUpdated}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="update-button"
                          onClick={() => {
                            const newRateValue = prompt(`Enter new rate for ${rate.currency}:`, rate.rate);
                            if (newRateValue && !isNaN(newRateValue)) {
                              handleUpdateRate(rate.currency, parseFloat(newRateValue));
                            }
                          }}
                          disabled={isLoading}
                        >
                          Update
                        </button>
                        <button
                          className="delete-button"
                          onClick={() => handleDeleteRate(rate.currency)}
                          disabled={isLoading}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="admin-footer">
        <button onClick={handleLogout} className="admin-logout-button">
          Logout
        </button>
      </div>
    </div>
  );
};

export default AdminExchangeRatesScreen;

